import * as React from "react";
import { graphql } from "react-relay";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { Button, useModals } from "~/ui";

import AddDelegateModal from "../DelegatedAccess/AddDelegateModal";
import DelegateCard, { AddDelegateCard } from "../DelegatedAccess/DelegateCard";
import { Divider, Label, Row, SectionTitle, SettingsPageSubtitle, SettingsPageTitle, SubLabel } from "./shared";

import type { DelegatedAccessModalQuery } from "~/__generated__/DelegatedAccessModalQuery.graphql";

import styles from "../DelegatedAccess/css/styles.module.css";

const { useCallback } = React;

const delegatedAccessModalQuery = graphql`
	query DelegatedAccessModalQuery {
		viewer {
			user {
				id
			}
			account {
				billingAccount {
					hasAccessToDelegatedAccess: hasAccessToFeature(feature: delegated_access)
				}
			}
			delegators {
				id
				delegator {
					id
				}
				...DelegateCard_Delegation
			}
			delegates {
				id
				delegate {
					id
				}
				...DelegateCard_Delegation
			}
		}
	}
`;

const ADD_DELEGATE_CARD_VISIBLE_ON_COUNT = 2;

export default function DelegatedAccess() {
	const queryData = useLazyLoadQuery<DelegatedAccessModalQuery>(delegatedAccessModalQuery, {}, {});

	const delegators = queryData.viewer.delegators;
	const delegates = queryData.viewer.delegates;
	const hasAccessToDelegatedAccess = queryData.viewer.account.billingAccount.hasAccessToDelegatedAccess;

	const modals = useModals();

	const excludeUsers = React.useMemo<string[]>(() => {
		const ids = delegates.map(delegation => delegation && delegation.delegate.id);
		ids.push(queryData.viewer.user.id);
		return ids;
	}, [delegates, queryData.viewer.user.id]);

	const addDelegateModal = React.useCallback(() => {
		modals.open(({ close }) => <AddDelegateModal excludeUsers={excludeUsers} onClose={close} />);
	}, [modals, excludeUsers]);

	const showDelegatedAccessFeatureModal = useCallback(() => showFeatureModal("delegated_access"), []);

	return (
		<div style={{ width: "100%", maxWidth: "40rem" }}>
			<SettingsPageTitle>Delegated access</SettingsPageTitle>
			<SettingsPageSubtitle>
				Enables another user to manage your meetings, tasks, and calendar on your behalf
			</SettingsPageSubtitle>
			<Divider />
			<SectionTitle>My delegates</SectionTitle>
			<Row>
				<Row.Left>
					<Label>Add your account delegates</Label>
					<SubLabel>A delegate can manage your meetings, tasks, calendar and more on your behalf.</SubLabel>
				</Row.Left>
				<Row.Right>
					{hasAccessToDelegatedAccess ? (
						<Button variant="primary" onClick={addDelegateModal}>
							<FontAwesomeIcon icon={faPlus} fontSize={12} />
							&ensp;Add
						</Button>
					) : (
						<Button variant="billing" onClick={showDelegatedAccessFeatureModal}>
							<FontAwesomeIcon icon={billingUpgradeIconDef} />
							&ensp;Add
						</Button>
					)}
				</Row.Right>
			</Row>
			<div className={styles.delegatesList}>
				{hasAccessToDelegatedAccess &&
					delegates?.map(delegation =>
						delegation ? <DelegateCard isDelegate={true} delegation={delegation} key={delegation.id} /> : null,
					)}

				{delegates.length <= ADD_DELEGATE_CARD_VISIBLE_ON_COUNT && (
					<AddDelegateCard
						addDelegateCallback={hasAccessToDelegatedAccess ? addDelegateModal : showDelegatedAccessFeatureModal}
					/>
				)}
			</div>
			<SectionTitle>Accounts I can access</SectionTitle>
			{!delegators.length ? (
				<p style={{ margin: "1em 0", font: "var(--paragraph-5)" }}>
					All accounts that you have access to will be displayed here.
				</p>
			) : (
				<div className={styles.delegatesList}>
					{delegators?.map(delegation => {
						if (delegation?.delegator) {
							return <DelegateCard isDelegate={false} delegation={delegation} key={delegation.id} />;
						}
						return null;
					})}
				</div>
			)}
		</div>
	);
}
